import request from "../libs/request";

import {
  VOTE_ITEM_TYPE,
  VOTE_PERIOD,
  VOTE_STATUS,
} from "../consts/hotColdVote";

import { BuildingId } from "../types/building";
import { BuildingVocCategorySetting } from "../types/hotCold";

import { BuildingSpaceIdRequest } from "./buildingServices";
import { AirInfo, FloorAirInfo, SpaceAirInfo } from "./iotServices";
import { SatisfactionInfo } from "./spaceReservationServices";
import { Pageable, PageableRequest } from "./talkServices";

const baseUrl = "/hot-cold";
const voteUrl = baseUrl + "/vote";
const vocUrl = baseUrl + "/voc";

export type Vote = BuildingId & {
  voteId: number;
  buildingName: string;
  buildingFloorId: string;
  buildingFloorName: string;
  buildingSpaceId: string;
  buildingSpaceName: string;
  location: string;
  status: VoteStatus;
  serviceTemplateId: string;
  centerIncommodityId: string;
  voteDate: string;
  votePeriod: VotePeriod;
  result: string;
  createdBy: number;
  createdAt: string;
  createdByNickname: string;
  modifiedBy: number;
  modifiedAt: string;
  startedAt: string;
  endedAt: string;
  feedback: string | null;
  feedbackAt: string | null;
  feedbackBy: string | null;
  summary: VoteSummary;
  userVoteItem?: VoteItem | null;
  userSatisfactionInfo?: SatisfactionInfo | null;
  locale: string;
};

export type VotePeriod = "AM" | "PM";

export type VoteStatus = keyof typeof VOTE_STATUS;

export type VoteItemType = "HOT" | "COLD" | "GOOD";

export type VoteItem = {
  voteItemId: number;
  voteId: number;
  itemType: VoteItemType;
  createdBy: number;
  createdAt: string | null;
  modifiedBy: number | null;
  modifiedAt: string | null;
};

export type VoteSpace = BuildingId & {
  buildingName: string;
  buildingFloorId: string;
  buildingFloorName: string;
  buildingSpaceId: string;
  buildingSpaceName: string;
  vote: VoteSpaceStatus | null;
  isVoteCreatable: true;
};

export type VoteSpaceStatus = {
  voteId: number;
  location: string;
  status: VoteStatus;
  votePeriod: VotePeriod;
  centerIncommodityId: string;
};

export type VoteSummary = {
  voteId: number;
  coldCount: number;
  hotCount: number;
  goodCount: number;
};

export type VoteRecentInfo = {
  [key: number]: string[];
};

export type VoteIdRequest = {
  voteId: number;
};

export const getHotColdVote = (params: VoteIdRequest) =>
  request.get<Vote>(`${voteUrl}/${params.voteId}`);

export const getHotColdVoteStatus = (params: BuildingSpaceIdRequest) =>
  request.get<VoteSpace>(
    `${voteUrl}/building-space/${params.buildingSpaceId}/status`
  );

export type HotColdCreateRequest = {
  buildingSpaceId: string;
  location: string;
  itemType: VoteItemType;
  duration: number;
};

export const postHotColdVote = (params: HotColdCreateRequest) =>
  request.post(`${voteUrl}`, params);

export type VoteItemRequest = {
  voteId: number;
  itemType: VoteItemType;
};

export const postHotColdVoteItem = (params: VoteItemRequest) =>
  request.post(`${voteUrl}/${params.voteId}/item`, params);

export type HotColdVoteListRequest = PageableRequest & {
  voteId?: number;
  buildingId?: string;
  buildingFloorId?: string;
  buildingSpaceId?: string;
  location?: string;
  status?: VoteStatus;
  voteDate?: string;
  votePeriod?: VotePeriod;
  createdBy?: string;
  voteItemId?: number;
  onlyMyVote?: boolean;
  sortOrder?: string;
};

export type HotColdVoteResponse = {
  pageable: Pageable;
  items: Vote[];
};

export const getHotColdVoteList = (params: HotColdVoteListRequest) =>
  request.get<HotColdVoteResponse>(`${voteUrl}/list`, { params });

export type VotePeriodInfo = {
  voteId: number;
  status: string;
  result: string;
  topRate: number;
};

export type EquipmentStatus = "HOT" | "COLD" | "OFF";

export type VentilationStatus = "ON" | "OFF";

export type HotColdSpaceAirInfo = SpaceAirInfo & {
  ventilationStatus: VentilationStatus | null;
  equipmentStatus: EquipmentStatus | null;
  am: VotePeriodInfo | null;
  pm: VotePeriodInfo | null;
};

export type HotColdFloorAirInfo = Omit<FloorAirInfo, "spaces"> & {
  spaces: HotColdSpaceAirInfo[];
};

export type HotColdAirInfo = Omit<AirInfo, "floors"> & {
  floors: HotColdFloorAirInfo[];
};

export type HotColdSensor = {
  equipmentStatus: EquipmentStatus;
  ventilationStatus: VentilationStatus;
};

export const getPeriodString = (period?: VotePeriod) => {
  if (!period) return "";

  return period === VOTE_PERIOD.AM ? "오전" : "오후";
};

export const isWeekendOrAfter6PM = (): boolean => {
  const now = new Date();
  const currentHour = now.getHours();
  const currentDayOfWeek = now.getDay();

  const isWeekend = currentDayOfWeek === 0 || currentDayOfWeek === 6;
  const isAfter6PM = currentHour >= 18;

  return isWeekend || isAfter6PM;
};

export const getVoteItemTypeLabel = (type?: VoteItemType) => {
  if (!type) return "";

  return {
    [VOTE_ITEM_TYPE.HOT]: "더워요",
    [VOTE_ITEM_TYPE.COLD]: "추워요",
    [VOTE_ITEM_TYPE.GOOD]: "괜찮아요",
  }[type];
};

export const getVoteStatusLabel = (status?: VoteStatus) => {
  if (!status) return "대기상태";

  return {
    ["WAITING" as keyof VoteStatus]: "대기상태",
    [VOTE_STATUS.ONGOING]: "실시간 반응",
    [VOTE_STATUS.COMPLETED]: "투표 결과",
  }[status];
};

export const getBuildingVocCategorySetting = (params: BuildingId) =>
  request.get<BuildingVocCategorySetting>(`${vocUrl}/category-setting`, {
    params,
  });
